/* eslint-disable no-await-in-loop */
/**
 * Custom Category Banner Upload (isolated)
 *
 * Uploads a user-picked image to S3 under the `custom_background_images/` folder
 * and returns the full public https URL. This is intentionally kept OUT of the
 * global upload manager (uploadManagerSecure) so a banner never shows up in the
 * app's "uploading…" progress list / toast queue — a banner isn't a video.
 *
 * Flow (mirrors the mobile app spec):
 *   1. POST /api/upload/sessions/        (createUploadSession with custom s3_key)
 *   2. PUT  to presigned S3 URL(s)       (uploadPartWithPresignedUrl)
 *   3. POST /api/upload/sessions/{id}/complete/
 *   4. Build full URL = S3 base + s3_key  → caller sends it as the category `icon`
 */

import {
  createUploadSession,
  completeUploadSession,
  uploadPartWithPresignedUrl,
} from '../Api/uploaderApiFunctions';
import { generateS3Url } from '../Api/awsConfig';

const CUSTOM_BANNER_FOLDER = 'custom_background_images/';

/**
 * Build the S3 key for a banner: custom_background_images/{timestamp}_{first5}{ext}
 * (Filename convention differs harmlessly from mobile; only the folder + full URL matter.)
 */
function generateBannerS3Key(filename) {
  const timestamp = Date.now();
  const dotIndex = filename.lastIndexOf('.');
  const ext = dotIndex >= 0 ? filename.slice(dotIndex) : '';
  const nameNoExt = dotIndex >= 0 ? filename.slice(0, dotIndex) : filename;
  const cleanName = nameNoExt.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5) || 'image';
  const cleanExt = ext.replace(/[^a-zA-Z0-9.]/g, '');
  return `${CUSTOM_BANNER_FOLDER}${timestamp}_${cleanName}${cleanExt}`;
}

/**
 * Upload a custom banner image and return its full public https URL.
 *
 * @param {File} file - image file picked by the user
 * @param {Function} [onProgress] - optional (percent:number) => void
 * @returns {Promise<string>} full S3 URL of the uploaded banner
 */
export async function uploadCustomBanner(file, onProgress) {
  const s3Key = generateBannerS3Key(file.name);

  const session = await createUploadSession({
    file_name: file.name,
    file_size: file.size,
    content_type: file.type || 'image/jpeg',
    s3_key: s3Key,
  });

  const presignedUrls = session.presigned_urls || [];
  const partSize = session.part_size || file.size;
  const totalParts = session.total_parts || 1;

  const parts = [];
  for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    const presignedUrlObj = presignedUrls.find(
      (u) => u.part_number === partNumber || u.partNumber === partNumber
    );
    if (!presignedUrlObj) {
      throw new Error(`Missing presigned URL for part ${partNumber}`);
    }
    const presignedUrl = presignedUrlObj.presigned_url || presignedUrlObj.url;

    const part = await uploadPartWithPresignedUrl(
      presignedUrl,
      blob,
      partNumber,
      (loaded, total) => {
        if (onProgress && total) {
          const overall = ((start + loaded) / file.size) * 100;
          onProgress(Math.min(99, Math.round(overall)));
        }
      }
    );
    parts.push(part);
  }

  const sortedParts = parts.sort((a, b) => a.PartNumber - b.PartNumber);
  await completeUploadSession(session.id, sortedParts);

  if (onProgress) onProgress(100);

  // Full public URL = S3 base (https://projector-optimized.s3.us-west-2.amazonaws.com/) + key
  return `${generateS3Url()}${s3Key}`;
}

export default uploadCustomBanner;
